import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { ProductCard } from '@components/Home/MyProducts/ProductCard'
import { Slider } from '@components/Slider'
import { MEMBERS_PAGE_COURSE_CONTENTS } from '@constants/features'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'
import { ICourseModulesNodes } from '@dtos/Course'
import { IFeaturedProduct } from '@dtos/MembersConfig'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useCourseContents } from '@hooks/useCourseContents'
import { useCurrentLesson } from '@hooks/useCurrentLesson'
import { useEnrollment } from '@hooks/useEnrollment'
import { useLinearProgressGate } from '@hooks/useLinearProgressGate'
import { useMediaSize } from '@hooks/useMediaSize'
import { useModules } from '@hooks/useModules'
import { useHasFeature } from '@hooks/useThemeParams'
import { filterAvailableModules } from '@utils/courseModules'
import {
  courseContentPath,
  heroThemeCourseContentPath,
} from '@utils/coursePaths'
import { moduleToProductCardData } from '@utils/moduleCardData'
import {
  ModuleTargetLesson,
  moduleTargetFromLoaded,
  resolveModuleTargetLesson,
} from '@utils/moduleTargetLesson'
import { MODULES_PAGE_SIZE } from '@utils/submodulesPagination'
import { router } from 'expo-router'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowDimensions } from 'react-native'
import { isWeb, useMedia, YStack } from 'tamagui'

interface IModulesCardsSectionProps {
  title?: string | null
  featuredProduct: Pick<IFeaturedProduct, 'productLogo' | 'productLogoVertical'>
  showModuleName?: boolean
}

/**
 * Seção "Módulos" da home focada (FR-017): todos os módulos do curso em cards
 * no padrão do card de produto, sem "Ver tudo". O toque abre a próxima aula
 * não concluída do módulo.
 */
export function ModulesCardsSection({
  title,
  featuredProduct,
  showModuleName = true,
}: IModulesCardsSectionProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [lastRequestedLength, setLastRequestedLength] = useState<number>()
  const { t } = useTranslation('FeaturedProductHome')
  const { modules, onLoadMore } = useModules()
  const { enrollment } = useEnrollment()
  const { isContentBlocked } = useLinearProgressGate()
  const { getCourseContents } = useCourseContents()
  const { courseId } = useCourseRouteParams()
  const { isPreview } = useAdminPreview()
  const { setCurrentLesson } = useCurrentLesson()
  const hasCourseContentsPage = useHasFeature(MEMBERS_PAGE_COURSE_CONTENTS)
  const media = useMedia()
  const sizes = useMediaSize()
  const { width: windowWidth } = useWindowDimensions()

  const containerPadding = media.gtSm ? 48 : 16
  const spaceBetweenCards = media.gtSm ? 20 : 16
  const cardWidth =
    CARD_DIMENSIONS.vertical[sizes]?.width || CARD_DIMENSIONS.vertical.xs.width
  const slidesPerView =
    (windowWidth - containerPadding) / (cardWidth + spaceBetweenCards)

  const loadedModules = modules?.nodes
  const availableModules = useMemo(
    () => filterAvailableModules(loadedModules ?? []),
    [loadedModules],
  )
  const loadedCount = loadedModules?.length ?? 0

  // O ModulesContext não diz quantos módulos o curso tem: página cheia sugere
  // que há mais, e um pedido que não trouxe nada novo encerra a lista.
  const hasMore =
    loadedCount > 0 &&
    loadedCount % MODULES_PAGE_SIZE === 0 &&
    lastRequestedLength !== loadedCount

  const handleLoadMore = useCallback(() => {
    if (!hasMore) return
    setLastRequestedLength(loadedCount)
    onLoadMore?.()
  }, [hasMore, loadedCount, onLoadMore])

  const fetchMoreContents = useCallback(
    async (moduleId: number, offset: number) => {
      const data = await getCourseContents?.(`${moduleId}`, offset)
      return data?.course.courseModules?.nodes[0]?.courseContents?.nodes
    },
    [getCourseContents],
  )

  const openTarget = useCallback(
    (target: ModuleTargetLesson) => {
      if (target.kind !== 'lesson' || !enrollment) return
      const { content, moduleId, parentCourseModuleId } = target

      // Mesmo objeto que o LessonCard grava: a aula com o contexto de
      // liberação da matrícula.
      const dataAvailable = {
        createdAt: enrollment.activatedAt,
        courseClassRelease: enrollment.courseClassRelease,
      }
      setCurrentLesson({ ...content.lesson, ...dataAvailable })

      // Mesmo desvio do LessonCard: sem a página de consumo no members, a aula
      // abre no hero_theme.
      if (isWeb && !isPreview && !hasCourseContentsPage) {
        window.location.href = heroThemeCourseContentPath({
          enrollmentId: enrollment.id,
          courseId,
          courseContentId: content.id,
        })
        return
      }

      let path = courseContentPath({
        isPreview,
        enrollmentId: enrollment.id,
        courseId,
        courseContentId: content.id,
        moduleId,
      })
      if (parentCourseModuleId) {
        path += `&parentCourseModuleId=${parentCourseModuleId}`
      }
      router.navigate(path)
    },
    [enrollment, courseId, isPreview, hasCourseContentsPage, setCurrentLesson],
  )

  const handleModulePress = useCallback(
    async (module: ICourseModulesNodes) => {
      if (!enrollment) return
      const target = await resolveModuleTargetLesson({
        module,
        enrollment,
        isContentBlocked,
        fetchMoreContents,
      })
      openTarget(target)
    },
    [enrollment, isContentBlocked, fetchMoreContents, openTarget],
  )

  if (!enrollment || availableModules.length === 0) return null

  return (
    <YStack
      testID="featured-modules-section"
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Slider
        slidesTotalNumber={availableModules.length}
        slidesPerView={slidesPerView}
        displaySliderControls={
          media.gtSm && availableModules.length > slidesPerView
        }
        displaySliderControlsHorizontal={media.gtMd && isHovered}
        slidesSpacing={spaceBetweenCards}
        navigationControlVariation="dots"
        loadMoreContent={handleLoadMore}
        hasMore={hasMore}
        contentInset={containerPadding}
        sliderTitle={
          <DefaultSectionTitle
            title={title || t('modules.defaultTitle')}
            onButtonPress={() => null}
            isHoveringSection={isHovered}
            showButton={false}
          />
        }
      >
        {availableModules.map((module) => {
          const cardData = moduleToProductCardData({
            module,
            lessonsCompletedPerModule: enrollment.lessonsCompletedPerModule,
            featuredProduct,
          })
          const loadedTarget = moduleTargetFromLoaded({
            module,
            enrollment,
            isContentBlocked,
          })
          const isAvailable =
            cardData.isAvailable &&
            loadedTarget.kind !== 'blocked' &&
            loadedTarget.kind !== 'empty'

          return (
            <ProductCard
              key={cardData.id}
              isHorizontal={false}
              hasAuxiliarTitle={showModuleName}
              data={cardData}
              isAvailable={isAvailable}
              customOnPress={() => handleModulePress(module)}
              accessibilityRole="button"
              accessibilityLabel={t('modules.cardAccessibilityLabel', {
                name: module.name,
              })}
            />
          )
        })}
      </Slider>
    </YStack>
  )
}
